import { useSnackbar } from "notistack";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import logoUrl from "@/assets/fairfleet-logo.png";
import { useGeotabClient } from "@/components/GeotabClientContext";
import {
  buildReportSheets,
  Report,
  ReportContext,
  reportFileName,
} from "@/utilities/export/report";
import { createWorkbook, downloadBlob } from "@/utilities/xlsx";

/** Display size of the logo in the sheet; the PNG asset is higher-resolution for sharpness. */
const LOGO_WIDTH_PX = 180;
const LOGO_HEIGHT_PX = 39;

const logoCache = new Map<string, Promise<Uint8Array | undefined>>();

/**
 * Fetches an image once per URL and page load. Never throws: a failed load logs and resolves to
 * `undefined`, and is not cached, so the next export tries again.
 */
export function loadLogo(url: string): Promise<Uint8Array | undefined> {
  const cached = logoCache.get(url);
  if (cached) return cached;

  const loading = fetch(url)
    .then(async response => {
      if (!response.ok) throw new Error(`Logo request failed with status ${response.status}`);
      return new Uint8Array(await response.arrayBuffer());
    })
    .catch((error: unknown) => {
      console.error("Excel export: logo could not be loaded", error);
      logoCache.delete(url);
      return undefined;
    });
  logoCache.set(url, loading);
  return loading;
}

/** Builds the standard report workbook for the active session and downloads it. */
export function useExcelExport(): { exportExcel: (report: Report) => Promise<void> } {
  const { database, username } = useGeotabClient();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const exportExcel = useCallback(
    async (report: Report) => {
      try {
        const context: ReportContext = { database, userName: username, exportedAt: new Date(), t };
        const logoData = await loadLogo(logoUrl);
        const logo = logoData && {
          data: logoData,
          anchorColumn: 0,
          widthPx: LOGO_WIDTH_PX,
          heightPx: LOGO_HEIGHT_PX,
        };
        downloadBlob(
          createWorkbook(buildReportSheets(report, context, logo)),
          reportFileName(report, context),
        );
      } catch (error) {
        console.error(error);
        enqueueSnackbar(t("common.exportError"), { variant: "error" });
      }
    },
    [database, username, t, enqueueSnackbar],
  );

  return { exportExcel };
}
